/**
 * Container Resource Management Hooks
 *
 * React Query hooks for container CRUD operations.
 * Handles caching, invalidation, and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { resourceContainerService } from '../services/resource-container.service';
import type {
  CreateContainerDTO,
  AssignContainerDTO,
  RetrieveContainerDTO,
  ReturnToYardDTO,
  MarkForMaintenanceDTO,
  ContainerFilterDTO,
} from '../schemas/containerSchema';

const CONTAINERS_KEY = ['resource-containers'] as const;
const CONTAINER_SUMMARY_KEY = ['resource-containers', 'summary'] as const;

/**
 * Hook to fetch containers list with optional filters
 */
export function useContainers(filters?: ContainerFilterDTO) {
  return useQuery({
    queryKey: [...CONTAINERS_KEY, filters],
    queryFn: () => resourceContainerService.list(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch a single container by ID
 */
export function useContainer(id: string | undefined) {
  return useQuery({
    queryKey: [...CONTAINERS_KEY, id],
    queryFn: () => resourceContainerService.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to fetch container summary statistics
 */
export function useContainerSummary() {
  return useQuery({
    queryKey: CONTAINER_SUMMARY_KEY,
    queryFn: () => resourceContainerService.getSummary(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to create a new container
 */
export function useCreateContainer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContainerDTO) => resourceContainerService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create container');
    },
  });
}

/**
 * Hook to update container details
 */
export function useUpdateContainer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateContainerDTO> }) =>
      resourceContainerService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update container');
    },
  });
}

/**
 * Hook to assign container to a job
 */
export function useAssignContainer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignContainerDTO }) =>
      resourceContainerService.assignToJob(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container assigned to job successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to assign container');
    },
  });
}

/**
 * Hook to retrieve container from job
 */
export function useRetrieveContainer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RetrieveContainerDTO }) =>
      resourceContainerService.retrieveFromJob(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container retrieved successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to retrieve container');
    },
  });
}

/**
 * Hook to return container to yard
 */
export function useReturnToYard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReturnToYardDTO }) =>
      resourceContainerService.returnToYard(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container returned to yard successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to return container');
    },
  });
}

/**
 * Hook to mark container for maintenance
 */
export function useMarkForMaintenance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MarkForMaintenanceDTO }) =>
      resourceContainerService.markForMaintenance(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container marked for maintenance');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to mark container for maintenance');
    },
  });
}

/**
 * Hook to retire container
 */
export function useRetireContainer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => resourceContainerService.retire(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container retired successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to retire container');
    },
  });
}

/**
 * Hook to delete container (hard delete)
 */
export function useDeleteContainer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => resourceContainerService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTAINERS_KEY });
      queryClient.invalidateQueries({ queryKey: CONTAINER_SUMMARY_KEY });
      toast.success('Container deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete container');
    },
  });
}
